import { type Auth0Client, createAuth0Client } from "@auth0/auth0-spa-js";
import { saveSession } from "@platform/user/client";

import type { Auth0Tenant } from "./signIn";

// The portal's Auth0 connection for Centro staff.
const CENTRO_WORKPLACE_CONNECTION = "CentroWorkplace";

/**
 * Whether the URL is Auth0 returning from sign-in, which it does to the app's root: with
 * a code on success, or an error (access_denied, say) on failure.
 */
export const isAuth0Callback = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.has("state") && (params.has("code") || params.has("error"));
};

interface LoginAppState {
  readonly returnTo: string;
}

let client: Promise<Auth0Client> | undefined;

// The audience is the client id, as the user lib's SSO client expects: the SDK keys
// its token cache by audience, so both clients must agree on it. Auth0 returns to the
// root, the redirect URI the old golden UI registered (ai-docs golden-ui/deploy/envs).
const auth0Client = (tenant: Auth0Tenant): Promise<Auth0Client> => {
  client ??= createAuth0Client({
    domain: tenant.domain,
    clientId: tenant.clientId,
    authorizationParams: {
      audience: tenant.clientId,
      redirect_uri: window.location.origin,
    },
    cacheLocation: "localstorage",
    useRefreshTokens: true,
  });
  return client;
};

export const loginWithAuth0 = async (
  tenant: Auth0Tenant,
  returnTo: string,
): Promise<void> =>
  (await auth0Client(tenant)).loginWithRedirect<LoginAppState>({
    authorizationParams: { connection: CENTRO_WORKPLACE_CONNECTION },
    appState: { returnTo },
  });

const completeLogin = async (
  tenant: Auth0Tenant,
  callbackUrl: string,
): Promise<string> => {
  const auth0 = await auth0Client(tenant);
  const { appState } =
    await auth0.handleRedirectCallback<LoginAppState>(callbackUrl);
  const [accessToken, idToken] = await Promise.all([
    auth0.getTokenSilently(),
    auth0.getIdTokenClaims(),
  ]);
  // The SDK keeps the refresh token in its own cache, so an SSO session stays signed in.
  saveSession(
    { accessToken, idToken: idToken?.__raw },
    { keepSignedIn: true, provider: "sso" },
  );
  return appState?.returnTo ?? "/";
};

let pendingCallback:
  | { readonly url: string; readonly result: Promise<string> }
  | undefined;

/**
 * Stores the session Auth0 redirected back with and resolves to the page to return to.
 * Auth0 accepts each authorization code once, so a callback page that mounts twice
 * shares the first call's result.
 */
export const handleAuth0Callback = (
  tenant: Auth0Tenant,
  callbackUrl: string,
): Promise<string> => {
  if (pendingCallback?.url !== callbackUrl) {
    pendingCallback = {
      url: callbackUrl,
      result: completeLogin(tenant, callbackUrl),
    };
  }
  return pendingCallback.result;
};
